import { faHeadSideBrain } from '@fortawesome/duotone-regular-svg-icons';
import {
  faCheckCircle,
  faFlag,
  faInfoCircle,
} from '@fortawesome/free-solid-svg-icons';
import {
  faBrakeWarning,
  faDiamondExclamation,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import ChakraAlert from '~/common/components/Alerts/Alert/Alert';
import AlertDescription from '~/common/components/Alerts/AlertDescription';
import AlertTitle from '~/common/components/Alerts/AlertTitle';
import Box from '~/common/components/Box';
import Notice from '~/common/mui/MUINotice/Notice';

type AlertProps = {
  message: React.ReactNode;
  title?: string;
  style?: any;
  severity:
    | 'info'
    | 'warning'
    | 'success'
    | 'error'
    | 'manualreview'
    | 'fadeAlert'
    | 'extractionFailed';
  isNotice?: boolean;
  sx?: any;
};

export default function Alert({
  message,
  title,
  severity,
  isNotice,
  sx,
}: Readonly<AlertProps>) {
  let alertColor, alertIcon, alertBackground, status, borderColor;

  if (!message) {
    return null;
  }

  if (isNotice) {
    return (
      <Notice
        severity={severity}
        message={message}
        sx={{ marginBottom: '1rem', ...sx }}
      />
    );
  }

  switch (severity) {
    case 'info':
      alertColor = 'blue.600';
      alertBackground = 'blue.50';
      alertIcon = faHeadSideBrain;
      title = title ?? 'Tip:';
      break;
    case 'warning':
      alertColor = 'orange.700';
      alertBackground = 'orange.100';
      alertIcon = faDiamondExclamation;
      title = title ?? 'New Condition:';
      break;
    case 'error':
      alertColor = 'red.700';
      alertBackground = 'red.100';
      alertIcon = faBrakeWarning;
      title = title ?? 'Error:';
      break;
    case 'success':
      alertColor = 'green.700';
      alertBackground = 'green.50';
      alertIcon = faCheckCircle;
      title = title || 'All Set:';
      break;
    case 'extractionFailed':
    case 'manualreview':
      alertBackground = 'orange.300';
      alertIcon = faFlag;
      title = title ?? 'Manual Review:';
      borderColor = 'orange.900';
      break;
    case 'fadeAlert':
      alertBackground = 'gray.200';
      alertColor = 'gray.600';
      alertIcon = faDiamondExclamation;
      title = title || 'Manual Review:';
      borderColor = 'gray.900';
      break;
    default:
      status = 'info';
      alertColor = 'blue.700';
      alertBackground = 'blue.50';
      alertIcon = faInfoCircle;
      title = title || 'Tip:';
      break;
  }
  return (
    <ChakraAlert
      border="1px solid"
      borderColor={severity === 'extractionFailed' ? borderColor : alertColor}
      background={alertBackground}
      borderRadius="4px"
      status={status}
      w="100%"
      mb="1rem"
      sx={sx}
    >
      <Box color={`${alertColor}`} mr={2}>
        <FontAwesomeIcon icon={alertIcon} />
      </Box>
      {title && (
        <AlertTitle mr={4} fontSize="sm" color={alertColor} whiteSpace="nowrap">
          {title}
        </AlertTitle>
      )}
      <AlertDescription fontSize="sm" lineHeight="24px">
        {message}
      </AlertDescription>
    </ChakraAlert>
  );
}
